import { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { useCRUD } from "../../api/crud";
import { ERROR_SNACKBAR, EXTERNAL_SERVICES_ENDPOINT, SUCCESS_SNACKBAR } from "../../consts";
import { ExternalService, ExternalServiceArea } from "../../types/externalServices";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import ExternalServiceAreasField, { AreaErrors } from "./externalServiceAreas/externalServiceAreasField.component";

type FieldErrors = Record<string, unknown>;

const firstMessage = (value: unknown): string | undefined => {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const first = value[0];
  if (typeof first === "string") return first;
  return (first as { message?: string })?.message ?? undefined;
};

/** Per-area messages, from the `areas: [{radius_km: [...]}, ...]` shape DRF nests them in. */
const areaErrorsFrom = (value: unknown): AreaErrors | undefined => {
  if (!Array.isArray(value)) return undefined;
  return value.map((row) =>
    row && typeof row === "object" && !Array.isArray(row)
      ? Object.fromEntries(
          Object.entries(row as Record<string, unknown>).map(([field, messages]) => [field, firstMessage(messages)])
        )
      : null
  );
};

interface ExternalServiceFormProps {
  id?: string | number;
  onSuccess?: (externalService?: ExternalService) => void;
}

export default function ExternalServiceForm({ id, onSuccess }: ExternalServiceFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { get, create, updatePUT, loading } = useCRUD(EXTERNAL_SERVICES_ENDPOINT);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [areas, setAreas] = useState<ExternalServiceArea[]>([]);
  const [errors, setErrors] = useState<FieldErrors | null>(null);
  const [initializing, setInitializing] = useState(Boolean(id));

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    get(id)
      .then((response) => {
        const data = response?.data as ExternalService | undefined;
        if (cancelled || !data) return;
        setName(data.name ?? "");
        setEmail(data.email ?? "");
        setPhoneNumber(data.phone_number ?? "");
        setNotes(data.notes ?? "");
        setAreas(data.areas ?? []);
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, get]);

  const handleSubmit = async () => {
    setErrors(null);
    const payload = {
      name,
      email: email.trim() || null,
      phone_number: phoneNumber.trim() || null,
      notes: notes.trim() || null,
      areas: areas.map(({ city, county, region, latitude, longitude, radius_km }) => ({
        city,
        county,
        region,
        latitude,
        longitude,
        radius_km,
      })),
    };

    const response = id ? await updatePUT({ id, ...payload }) : await create(payload);
    if (response?.success) {
      enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
      onSuccess?.(response.data as ExternalService);
      return;
    }

    const errorData = (response?.errorData ?? {}) as FieldErrors;
    setErrors(errorData);
    enqueueSnackbar(
      (typeof errorData.message === "string" && errorData.message) ||
        t("There was an error, please review the form."),
      ERROR_SNACKBAR
    );
  };

  if (initializing) {
    return <CustomCircularProgress />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, "& .MuiInputBase-root": { color: "var(--textColor)" } }}>
      <TextField
        size="small"
        label={t("Name")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        fullWidth
        error={Boolean(firstMessage(errors?.name))}
        helperText={firstMessage(errors?.name)}
      />

      <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
        <TextField
          size="small"
          label={t("Email")}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          error={Boolean(firstMessage(errors?.email))}
          helperText={firstMessage(errors?.email)}
        />
        <TextField
          size="small"
          label={t("Phone number")}
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          fullWidth
          error={Boolean(firstMessage(errors?.phone_number))}
          helperText={firstMessage(errors?.phone_number)}
        />
      </Box>

      <TextField
        size="small"
        label={t("Notes")}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        error={Boolean(firstMessage(errors?.notes))}
        helperText={firstMessage(errors?.notes)}
      />

      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
        <Typography variant="h6" sx={{ color: "var(--textColor)" }}>
          {t("Service area")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("The towns the service works in and how far around each. Jobs at an address inside a circle suggest this service first.")}
        </Typography>
        <ExternalServiceAreasField value={areas} onChange={setAreas} errors={areaErrorsFrom(errors?.areas)} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" disabled={loading} onClick={() => void handleSubmit()}>
          {id ? t("Update") : t("Submit")}
        </Button>
      </Box>
    </Box>
  );
}
